import { useState } from 'react';
import './ConsentForm.css';

const translations = {
  en: {
    title: 'Patient Consent for ADR Reporting',
    intro: 'You are required to fill your valid information in this form.',
    share: 'If you are willing to share your information with us, please click Yes.',
    decline: 'If not, please click No.',
    note: 'Make sure if the patient clicks Yes then the form will open. If No is selected, the form will remain closed.',
    yes: 'Yes, I Agree',
    no: 'No, I Decline',
    privacy: 'Your data is kept confidential and used solely for improving medication safety.',
  },
  gu: {
    title: 'ADR રિપોર્ટિંગ માટે દર્દીનું સંમતિ ફોર્મ',
    intro: 'આ ફોર્મમાં તમારી માન્ય માહિતી ભરવી આવશ્યક છે.',
    share: 'જો તમે અમને તમારી માહિતી શેર કરવા માટે તૈયાર છો તો કૃપા કરીને હા પર ક્લિક કરો.',
    decline: 'જો નહીં હોય તો કૃપા કરીને ના પર ક્લિક કરો.',
    note: 'ખાતરી કરો કે દર્દી હા ક્લિક કરે તો ફોર્મ ખુલશે. ના પસંદ કર્યા તો ફોર્મ બંધ રહેશે.',
    yes: 'હા, હું સંમત છું',
    no: 'ના, હું અસંતોષ વ્યક્ત કરું છું',
    privacy: 'તમારી માહિતી ગોપનીય રાખવામાં આવે છે અને તેનો ઉપયોગ માત્ર દવાઓની સલામતી સુધારવા માટે જ કરવામાં આવે છે.'
  },
  hi: {
    title: 'ADR रिपोर्टिंग के लिए रोगी की सहमति',
    intro: 'इस फ़ॉर्म में अपनी वैध जानकारी भरना आवश्यक है।',
    share: 'यदि आप हमें अपनी जानकारी साझा करने के लिए तैयार हैं तो कृपया हाँ पर क्लिक करें।',
    decline: 'यदि नहीं, तो कृपया नहीं पर क्लिक करें।',
    note: 'सुनिश्चित करें कि यदि रोगी हाँ क्लिक करता है तो फॉर्म खुलेगा। यदि नहीं चुना जाता है, तो फ़ॉर्म बंद रहेगा।',
    yes: 'हाँ, मैं सहमत हूँ',
    no: 'नहीं, मैं असहमत हूँ',
    privacy: 'आपकी जानकारी गोपनीय रखी जाती है और इसका उपयोग केवल दवाओं की सुरक्षा में सुधार के लिए किया जाता है।'
  },
};

export default function ConsentForm({ onAccept, onDecline, onBack }) {
  const [language, setLanguage] = useState('en');
  const t = translations[language];

  return (
    <div className="consent-overlay">
      <div className="consent-modal animate-fade" style={{ position: 'relative' }}>
        {onBack && (
          <button 
            type="button"
            onClick={onBack}
            style={{ position: 'absolute', top: '16px', left: '16px', background: 'none', border: 'none', fontSize: '14px', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            ← Back
          </button>
        )}
        {/* Header */}
        <div className="consent-header">
          <div className="consent-logo">
            <span>🏥</span>
          </div>
          <h1 className="consent-clinic-name">MedSafety Connect</h1>
          <span className="consent-badge">Patient Care Portal</span>
        </div>

        {/* Language selector */}
        <div className="consent-language-switcher">
          <button
            type="button"
            className={`consent-language-btn${language === 'en' ? ' active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            English
          </button>
          <button
            type="button"
            className={`consent-language-btn${language === 'gu' ? ' active' : ''}`}
            onClick={() => setLanguage('gu')}
          >
            ગુજરાતી
          </button>
          <button
            type="button"
            className={`consent-language-btn${language === 'hi' ? ' active' : ''}`}
            onClick={() => setLanguage('hi')}
          >
            हिन्दी
          </button>
        </div>

        {/* Title */}
        <div className="consent-title-block">
          <h2 className="consent-title">{t.title}</h2>
          <div className="consent-divider"></div>
        </div>

        {/* Multi-language Intro Box */}
        <div className="consent-intro-multilang" style={{ background: '#f3f0ff', border: '1px solid #d0bfff', borderRadius: '8px', padding: '16px', marginBottom: '20px', fontSize: '0.9rem', lineHeight: '1.5', color: '#495057', textAlign: 'left' }}>
          <p style={{ margin: '0 0 8px 0' }}>This is the self reporting form for the side effect (adverse drug reaction) that occurs due to your ongoing medication/treatment.</p>
          <p style={{ margin: '0 0 8px 0' }}>આ તમારા ચાલુ દવા/સારવાર દરમિયાન થતી આડઅસર (Adverse Drug Reaction - ADR) વિશેની સ્વ-અહેવાલ (Self-Reporting) ફોર્મ છે. કૃપા કરીને તમને થયેલી આડઅસર વિશેની માહિતી આ ફોર્મમાં નોંધાવો.</p>
          <p style={{ margin: 0 }}>यह आपके चल रहे उपचार/दवा के दौरान होने वाले दुष्प्रभाव (Adverse Drug Reaction - ADR) के लिए स्व-रिपोर्टिंग (Self-Reporting) फॉर्म है। कृपया आपको हुए दुष्प्रभाव के संबंध में जानकारी इस फॉर्म में भरें।</p>
        </div>

        {/* Body */}
        <div className="consent-body">
          <div className="consent-info-box">
            <span className="consent-info-icon">⚠️</span>
            <p className="consent-info-text">
              {t.intro}
            </p>
          </div>

          <div className="consent-info-box consent-info-box--warn">
            <span className="consent-info-icon">📝</span>
            <p className="consent-info-text">
              {t.share}
              <br />
              {t.decline}
            </p>
          </div>

          <p className="consent-question">
            {t.note}
          </p>

          <div className="consent-privacy-note">
            <span>🔒</span>
            <span>{t.privacy}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="consent-actions">
          <button className="consent-btn consent-btn--no" onClick={onDecline}>
            ✕ &nbsp;{t.no}
          </button>
          <button className="consent-btn consent-btn--yes" onClick={onAccept}>
            ✓ &nbsp;{t.yes}
          </button>
        </div>
      </div>
    </div>
  );
}
